import { IpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import {
  FactoryPathLookupPreset,
  FactoryPreset,
  type PathLookupGraphPreset,
  type TopologyGraphPreset,
} from '@ipfabric/utils';
import { ipfArray } from '@ipfabric/utils/fp/ipfArray';
import { prop } from '@ipfabric/utils/fp/prop';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import { isPresetValid } from '../helpers/isPresetValid';

const presetLocalStorageKeys = {
  pathLookup: 'graphPathLookupPreset',
  topologyId: 'graphTopologyPresetId',
} as const;

// biome-ignore lint/correctness/noUndeclaredVariables: globally declared type
declare const localStorage: IpfLocalStorage<{
  [presetLocalStorageKeys.pathLookup]: string;
  [presetLocalStorageKeys.topologyId]: string;
}>;

const getEdgeIdsFromPreset = (preset: PathLookupGraphPreset) =>
  pipe(preset, prop('edges'), array.map(prop('id')));

const containsSameEdgesAsFactory = (preset: PathLookupGraphPreset) =>
  pipe(
    preset,
    getEdgeIdsFromPreset,
    ipfArray.equals(getEdgeIdsFromPreset(FactoryPathLookupPreset)),
  );

export function getPathLookupPresetFromLocalStorage(): PathLookupGraphPreset {
  const localStoragePreset = localStorage.getItem(
    presetLocalStorageKeys.pathLookup,
  );
  if (localStoragePreset) {
    const parsedLocalStoragePreset = JSON.parse(
      localStoragePreset,
    ) as PathLookupGraphPreset;
    // Return saved preset from local storage only if it is valid.
    // Preset could be deleted but it could still be in local storage.
    if (
      isPresetValid(parsedLocalStoragePreset) &&
      containsSameEdgesAsFactory(parsedLocalStoragePreset)
    ) {
      return parsedLocalStoragePreset;
    }
    removePathlookupPresetInLocalStorage();
  }

  return FactoryPathLookupPreset;
}

export function updatePathlookupPresetInLocalStorage(
  preset: PathLookupGraphPreset,
): void {
  if (isPresetValid(preset)) {
    localStorage.setItem(
      presetLocalStorageKeys.pathLookup,
      JSON.stringify(preset),
    );
  } else {
    throw new IpfError('INVALID_ARGUMENT', 'cannot save invalid preset');
  }
}

export const removePathlookupPresetInLocalStorage = (): void => {
  localStorage.removeItem(presetLocalStorageKeys.pathLookup);
};

export const getTopologyPresetFromLocalStorage = (
  presets: TopologyGraphPreset[],
): TopologyGraphPreset => {
  const savedPresetId = localStorage.getItem(presetLocalStorageKeys.topologyId);

  if (savedPresetId) {
    const foundPreset = presets.find(({ id }) => id === savedPresetId);

    if (foundPreset) {
      return foundPreset;
    }
    removeTopologyPresetInLocalStorage();
  }

  return FactoryPreset;
};

export const removeTopologyPresetInLocalStorage = (): void => {
  localStorage.removeItem(presetLocalStorageKeys.topologyId);
};

export const updateTopologyPresetInLocalStorage = (
  preset: TopologyGraphPreset,
): void => {
  localStorage.setItem(presetLocalStorageKeys.topologyId, preset.id);
};
